import { useNavigate } from '@tanstack/react-router';
import { Button, IconTile } from '../components/ui';
import { Logo } from '../components/Logo';
import { Screen } from '../components/Screen';
import { AuthHero } from '../components/AuthHero';
import { Scene } from '../art/Scene';
import { useLang } from '../lib/i18n';
import { useLayout } from '../lib/layout';

/**
 * Welcome. Phone: the brand row, then the animated Trikuta & Chenab scene filling the height that is left (it
 * crops sky first, so the peaks, the river and the auto always show), then the copy anchored to the bottom.
 * Tablet / desktop: the same scene as a split hero with the copy in a card beside it.
 */
export function Welcome() {
  const nav = useNavigate();
  const { lang, setLang, t } = useLang();
  const L = useLayout();
  const hindi = lang === 'hi';

  const brand = (
    <header className={L.wide ? undefined : 'zr-gutter'} style={{ flex: 'none', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Logo />
        <span style={{ fontSize: 18, lineHeight: '22px', fontWeight: 700, letterSpacing: '-0.01em' }}>Zoomo Rides</span>
      </div>
      <button
        type="button"
        aria-label={hindi ? t('welcome.english') : t('welcome.hindi')}
        onClick={() => setLang(hindi ? 'hinglish' : 'hi')}
        style={{ height: 44, padding: '0 16px', borderRadius: 22, border: '1px solid var(--line2)', background: 'transparent', color: 'var(--ink)', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}
      >
        {hindi ? 'English' : 'हिंदी'}
      </button>
    </header>
  );

  const copy = (
    <>
      <h1 className={`zr-fadein${L.wide ? ' zr-display' : ''}`} style={L.wide ? { marginTop: 'clamp(28px, 7vh, 72px)' } : { margin: 0, fontSize: 44, lineHeight: '46px', fontWeight: 900, letterSpacing: '-0.035em' }}>
        {t('welcome.l1')}
        <br />
        {t('welcome.l2')}
        <br />
        <span style={{ color: 'var(--acText)' }}>{t('welcome.l3')}</span>
      </h1>
      <p style={{ margin: '16px 0 0', fontSize: L.wide ? 18 : 16, lineHeight: L.wide ? '28px' : '24px', fontWeight: 500, color: 'var(--ink2)', maxWidth: 440 }}>{t('welcome.lead')}</p>
      <Button onClick={() => nav({ to: '/signin/phone' })} iconAfter="arrow" style={{ marginTop: 28 }}>
        {t('welcome.cta')}
      </Button>
      {/* driver sign-up and the Terms / Privacy pages don't exist yet: not shown (docs/OWNER-DECISIONS.md §8) */}
    </>
  );

  if (L.wide)
    return (
      <AuthHero name="welcome" label="Welcome">
        {brand}
        <div style={{ display: 'flex', flexDirection: 'column' }}>{copy}</div>
        <ul className="zr-stagger" style={{ margin: '28px 0 0', padding: '20px 0 0', listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, borderTop: '1px solid var(--line)' }}>
          {([['shield2', 'welcome.f1'], ['cash', 'welcome.f2'], ['share', 'welcome.f3']] as const).map(([icon, key]) => (
            <li key={key} style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, lineHeight: '18px', fontWeight: 700, color: 'var(--ink2)' }}>
              <IconTile icon={icon} size={34} />
              {t(key)}
            </li>
          ))}
        </ul>
      </AuthHero>
    );

  return (
    <Screen name="welcome" label="Welcome">
      <div className="zr-col zr-top">
        {brand}
        {/* the scene takes whatever height is left; it is drawn to crop sky first on a short phone */}
        <div className="zr-fill" style={{ position: 'relative', overflow: 'hidden', minHeight: 120 }}>
          <Scene />
        </div>
        <div style={{ flex: 'none', padding: '0 24px var(--sab)', display: 'flex', flexDirection: 'column' }}>{copy}</div>
      </div>
    </Screen>
  );
}
